import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Globe, Instagram, X, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { sendVerificationEmail, signUpWithGoogle, verifyEmailToken, getVerificationToken } from './firebase';
import { useSmoothScroll, useScrollReveal, useParallax } from './hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

// --- ASSETS ---
const ASSETS = {
  BONE_BLANK: "/images/Untitled-2.png",
  SOMA_LOGO: "/images/Untitled-2.png",
  SWEDISH_MADE: "/images/Swdeish Made.png",
  SWEDISH_MADE_TEXT: "/images/swedish made nicotine pouch text.png",
  LANDING_VIDEO: "/images/landing-video.mp4",
  LANDING_VIDEO_MOBILE: "/images/landing-video-mobile.mp4",
  PRODUCT_WHITE: "/images/SOMA WHITE.png",
  PRODUCT_GALLERY: [
    "/images/2.png",
    "/images/3.png",
    "/images/8.png",
    "/images/6.png"
  ],
  ABOUT_SOMA: "/images/about soma.jpg",
  SOMA_DECONSTRUCTED: "/images/SOMA WHITE DECONSTRUCTED BLANK BG.png"
};

// Check if mobile device
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// --- TRANSLATIONS ---
const TRANSLATIONS = {
  en: {
    hero: {
      title: <>Calm Outside.<br/>Focus Inside.</>,
      desc: "SOMA delivers Swedish-made nicotine pouches, combining Sweden's long snus tradition with clean ingredients, precise release, and a smooth, consistent experience.",
      learnMore: "LEARN MORE",
      getNotified: "GET NOTIFIED ON LAUNCH DAY"
    },
    product: {
      title: "Our Signature Flavor.",
      price: "KRW 30,000",
      specs: {
        nicotine: "Nicotine Content",
        pouches: "Pouches Per Can",
        flavor: "Flavor Profile",
        flavorDesc: "Crisp eucalyptus + cool mint",
        sensation: "Sensation",
        sensationDesc: "Clean, refreshing",
        aroma: "Aroma",
        aromaDesc: "Subtle, herbal, non-intrusive",
        madeIn: "Made In",
        madeInDesc: "Sweden"
      },
      packNote: "*Sold in packs of 2",
      naverStore: "NAVER STORE"
    },
    aboutPage: {
      title: "About SOMA",
      intro: "SOMA was created around a simple idea: nicotine should be clear, calm, and uncomplicated.",
      p1: "Many people already use nicotine as part of their daily rhythm — before work, during a break, after a meal, on the move. We focus on making that experience steady, discreet, and predictable, without smoke, odor, or interruption.",
      p2: "Our goal is to offer a product that supports routine and balance, not urgency or excess. SOMA is built for people who value consistency, personal pace, and small rituals that help them move through the day with intention.",
      subTitle: "Swedish Pouches",
      heritage1: "Modern nicotine pouches trace their origins to Sweden, where oral nicotine products have been part of daily life for decades.",
      heritage2: "That long history led to:",
      heritageList: [
        "Well-established production standards",
        "Controlled ingredient sourcing",
        "Regulated manufacturing environments",
        "A clearer understanding of how nicotine can be delivered without smoke"
      ],
      back: "BACK"
    },
    modal: {
      title: "GET NOTIFIED ON LAUNCH DAY",
      emailLabel: "YOUR EMAIL:",
      signUp: "SEND SIGN-UP LINK",
      orContinueWith: "Or continue with",
      google: "Sign up with Google",
      alreadySubscribed: "This email is already subscribed",
      success: "Sign-up link sent! Check your email.",
      successSimple: "Thank you for subscribing!",
      verified: "Email verified! You're subscribed."
    },
    footer: {
      emailHeader: "EMAIL SIGN UP",
      emailPlaceholder: "email@example.com",
      signUp: "SIGN UP",
      links: {
        instagram: "@somanicotine",
        terms: "Terms",
        privacy: "Privacy",
        about: "About Soma"
      },
      copyright: "SOMA © 2025",
      warning: "Nicotine is an addictive chemical."
    },
    terms: {
      title: "Terms of Service",
      back: "BACK",
      lastUpdated: "Last Updated: November 2025",
      sections: [
        {
          title: "1. Acceptance of Terms",
          content: "By accessing and using SOMA's website and services, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by these terms, please do not use our services."
        },
        {
          title: "2. Age Restriction",
          content: "You must be at least 19 years old to purchase SOMA products in South Korea. By using our services, you confirm that you meet this age requirement. We reserve the right to request proof of age at any time."
        },
        {
          title: "3. Product Information",
          content: "SOMA nicotine pouches contain nicotine, which is an addictive substance. Our products are intended for adult nicotine users only. Product descriptions, images, and specifications are provided for informational purposes and may be subject to change."
        },
        {
          title: "4. Health Warnings",
          content: "Nicotine is addictive and can be harmful to health. Our products are not designed to help quit smoking or as smoking cessation devices. If you are pregnant, nursing, have heart disease, high blood pressure, or other medical conditions, consult a physician before use."
        },
        {
          title: "5. Privacy",
          content: "Your use of our services is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices regarding your personal information."
        },
        {
          title: "6. Limitation of Liability",
          content: "SOMA shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services or products."
        },
        {
          title: "7. Changes to Terms",
          content: "We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services constitutes acceptance of the modified terms."
        },
        {
          title: "8. Contact",
          content: "For questions about these Terms of Service, please contact us at soma@somanicotine.com"
        }
      ]
    },
    privacy: {
      title: "Privacy Policy",
      back: "BACK",
      lastUpdated: "Last Updated: November 2025",
      sections: [
        {
          title: "1. Information We Collect",
          content: "We collect information you provide directly to us, including: email address when you sign up for notifications, name and contact information when you make a purchase, and payment information processed through secure third-party services."
        },
        {
          title: "2. How We Use Your Information",
          content: "We use the information we collect to: send you launch notifications and updates you've requested, process your orders and send related information, communicate with you about products, services, and promotions, and improve our website and services."
        },
        {
          title: "3. Information Sharing",
          content: "We do not sell, trade, or rent your personal information to third parties. We may share your information with: service providers who assist in our operations, legal authorities when required by law, and business partners with your consent."
        },
        {
          title: "4. Data Security",
          content: "We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure."
        },
        {
          title: "5. Cookies and Tracking",
          content: "We use cookies and similar tracking technologies to track activity on our website and improve user experience. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent."
        },
        {
          title: "6. Your Rights",
          content: "You have the right to: access your personal data, correct inaccurate data, request deletion of your data, opt-out of marketing communications, and withdraw consent at any time."
        },
        {
          title: "7. Data Retention",
          content: "We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected and to comply with legal obligations."
        },
        {
          title: "8. Contact Us",
          content: "If you have questions about this Privacy Policy or our data practices, please contact us at soma@somanicotine.com"
        }
      ]
    }
  },
  ko: {
    hero: {
      title: <>고요한 외면.<br/>집중된 내면.</>,
      desc: "SOMA는 스웨덴의 오랜 스누스 전통과 깨끗한 성분, 정밀한 릴리스 기술을 결합하여 부드럽고 일관된 경험을 선사하는 스웨덴산 니코틴 파우치입니다.",
      learnMore: "자세히 보기",
      getNotified: "출시 알림 받기"
    },
    product: {
      title: "시그니처 플레이버.",
      price: "KRW 30,000",
      specs: {
        nicotine: "니코틴 함량",
        pouches: "캔당 파우치 수",
        flavor: "향 프로필",
        flavorDesc: "상쾌한 유칼립투스 + 시원한 민트",
        sensation: "느낌",
        sensationDesc: "깔끔함, 상쾌함",
        aroma: "향",
        aromaDesc: "은은함, 허브향, 자극적이지 않음",
        madeIn: "제조국",
        madeInDesc: "스웨덴"
      },
      packNote: "*2팩 단위 판매",
      naverStore: "네이버 스토어"
    },
    aboutPage: {
      title: "About SOMA",
      intro: "SOMA는 단순한 아이디어에서 시작되었습니다: 니코틴은 명확하고, 차분하고, 복잡하지 않아야 한다.",
      p1: "많은 사람들이 이미 니코틴을 일상의 리듬으로 사용하고 있습니다 — 출근 전, 휴식 중, 식사 후, 이동 중에. 우리는 그 경험을 연기, 냄새, 방해 없이 안정적이고, 눈에 띄지 않으며, 예측 가능하게 만드는 데 집중합니다.",
      p2: "우리의 목표는 긴박함이나 과잉이 아닌, 루틴과 균형을 지원하는 제품을 제공하는 것입니다. SOMA는 일관성, 개인의 페이스, 그리고 하루를 의도적으로 보내는 데 도움이 되는 작은 의식을 소중히 여기는 사람들을 위해 만들어졌습니다.",
      subTitle: "Swedish Pouches",
      heritage1: "현대 니코틴 파우치의 기원은 스웨덴입니다. 스웨덴에서는 수십 년간 경구 니코틴 제품이 일상의 일부였습니다.",
      heritage2: "그 오랜 역사는 다음을 이끌어냈습니다:",
      heritageList: [
        "확립된 생산 기준",
        "통제된 원료 조달",
        "규제된 제조 환경",
        "연기 없이 니코틴을 전달하는 방법에 대한 명확한 이해"
      ],
      back: "뒤로 가기"
    },
    modal: {
      title: "출시 알림 받기",
      emailLabel: "이메일 주소:",
      signUp: "가입 링크 보내기",
      orContinueWith: "또는",
      google: "Google로 가입",
      alreadySubscribed: "이미 등록된 이메일입니다",
      success: "가입 링크가 발송되었습니다! 이메일을 확인하세요.",
      successSimple: "구독해 주셔서 감사합니다!",
      verified: "이메일 인증 완료! 구독되었습니다."
    },
    footer: {
      emailHeader: "이메일 구독",
      emailPlaceholder: "이메일 주소",
      signUp: "구독하기",
      links: {
        instagram: "@somanicotine",
        terms: "이용약관",
        privacy: "개인정보처리방침",
        about: "SOMA 이야기"
      },
      copyright: "SOMA © 2025",
      warning: "니코틴은 중독성 화학물질입니다."
    },
    terms: {
      title: "이용약관",
      back: "뒤로 가기",
      lastUpdated: "최종 업데이트: 2025년 11월",
      sections: [
        {
          title: "1. 약관 동의",
          content: "SOMA 웹사이트 및 서비스에 접속하고 이용함으로써, 귀하는 본 약관의 조건에 동의하고 이에 구속됩니다. 본 약관에 동의하지 않으시면 서비스를 이용하지 마십시오."
        },
        {
          title: "2. 연령 제한",
          content: "대한민국에서 SOMA 제품을 구매하려면 만 19세 이상이어야 합니다. 서비스 이용 시 귀하는 이 연령 요건을 충족함을 확인합니다. 당사는 언제든지 연령 증명을 요청할 권리가 있습니다."
        },
        {
          title: "3. 제품 정보",
          content: "SOMA 니코틴 파우치는 중독성 물질인 니코틴을 함유하고 있습니다. 당사 제품은 성인 니코틴 사용자만을 위한 것입니다. 제품 설명, 이미지 및 사양은 정보 제공 목적으로 제공되며 변경될 수 있습니다."
        },
        {
          title: "4. 건강 경고",
          content: "니코틴은 중독성이 있으며 건강에 해로울 수 있습니다. 당사 제품은 금연을 돕거나 금연 보조제로 설계되지 않았습니다. 임신 중이거나 수유 중인 경우, 심장병, 고혈압 또는 기타 질환이 있는 경우 사용 전 의사와 상담하십시오."
        },
        {
          title: "5. 개인정보",
          content: "서비스 이용 시 개인정보처리방침도 적용됩니다. 개인정보 처리에 관한 당사의 관행을 이해하려면 개인정보처리방침을 검토하십시오."
        },
        {
          title: "6. 책임 제한",
          content: "SOMA는 서비스 또는 제품의 사용 또는 사용 불가능으로 인한 간접적, 부수적, 특별, 결과적 또는 징벌적 손해에 대해 책임지지 않습니다."
        },
        {
          title: "7. 약관 변경",
          content: "당사는 언제든지 본 약관을 수정할 권리가 있습니다. 변경 사항은 웹사이트에 게시되는 즉시 효력이 발생합니다. 서비스를 계속 이용하시면 수정된 약관에 동의하는 것으로 간주됩니다."
        },
        {
          title: "8. 문의",
          content: "이용약관에 관한 문의는 soma@somanicotine.com으로 연락해 주십시오."
        }
      ]
    },
    privacy: {
      title: "개인정보처리방침",
      back: "뒤로 가기",
      lastUpdated: "최종 업데이트: 2025년 11월",
      sections: [
        {
          title: "1. 수집하는 정보",
          content: "당사는 귀하가 직접 제공하는 정보를 수집합니다. 여기에는 알림 신청 시 이메일 주소, 구매 시 이름 및 연락처 정보, 안전한 제3자 서비스를 통해 처리되는 결제 정보가 포함됩니다."
        },
        {
          title: "2. 정보 사용 방법",
          content: "수집된 정보는 다음 목적으로 사용됩니다: 요청하신 출시 알림 및 업데이트 발송, 주문 처리 및 관련 정보 전송, 제품, 서비스 및 프로모션에 관한 소통, 웹사이트 및 서비스 개선."
        },
        {
          title: "3. 정보 공유",
          content: "당사는 귀하의 개인정보를 제3자에게 판매, 거래 또는 임대하지 않습니다. 다음과 같은 경우에 정보를 공유할 수 있습니다: 운영을 지원하는 서비스 제공업체, 법률에 의해 요구되는 경우 법적 기관, 귀하의 동의하에 비즈니스 파트너."
        },
        {
          title: "4. 데이터 보안",
          content: "당사는 무단 접근, 변경, 공개 또는 파기로부터 개인정보를 보호하기 위해 적절한 보안 조치를 시행합니다. 그러나 인터넷을 통한 전송 방법은 100% 안전하지 않습니다."
        },
        {
          title: "5. 쿠키 및 추적",
          content: "당사는 웹사이트의 활동을 추적하고 사용자 경험을 개선하기 위해 쿠키 및 유사한 추적 기술을 사용합니다. 브라우저에서 모든 쿠키를 거부하거나 쿠키가 전송될 때 알림을 받도록 설정할 수 있습니다."
        },
        {
          title: "6. 귀하의 권리",
          content: "귀하는 다음과 같은 권리가 있습니다: 개인 데이터에 접근, 부정확한 데이터 수정, 데이터 삭제 요청, 마케팅 통신 수신 거부, 언제든지 동의 철회."
        },
        {
          title: "7. 데이터 보존",
          content: "당사는 수집 목적을 달성하고 법적 의무를 준수하는 데 필요한 기간 동안만 개인정보를 보유합니다."
        },
        {
          title: "8. 문의",
          content: "개인정보처리방침 또는 데이터 처리에 관한 문의는 soma@somanicotine.com으로 연락해 주십시오."
        }
      ]
    }
  }
};

// --- COMPONENTS ---

// Age Verification Screen (shown first)
const AgeVerifyScreen = ({ lang, setLang, onVerify }) => {
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [error, setError] = useState('');

  // Calculate Korean age (만 나이)
  const calculateKoreanAge = () => {
    const year = parseInt(birthYear);
    const month = parseInt(birthMonth);
    const day = parseInt(birthDay);

    if (!year || !month || !day) return null;

    const today = new Date();
    const birthDate = new Date(year, month - 1, day);

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const handleVerify = () => {
    const year = parseInt(birthYear);
    const month = parseInt(birthMonth);
    const day = parseInt(birthDay);

    if (!birthYear || !birthMonth || !birthDay) {
      setError(lang === 'ko' ? '생년월일을 모두 입력해주세요.' : 'Please enter your full date of birth.');
      return;
    }

    if (year < 1900 || year > new Date().getFullYear()) {
      setError(lang === 'ko' ? '올바른 연도를 입력해주세요.' : 'Please enter a valid year.');
      return;
    }

    if (month < 1 || month > 12) {
      setError(lang === 'ko' ? '올바른 월을 입력해주세요.' : 'Please enter a valid month.');
      return;
    }

    if (day < 1 || day > 31) {
      setError(lang === 'ko' ? '올바른 일을 입력해주세요.' : 'Please enter a valid day.');
      return;
    }

    const age = calculateKoreanAge();

    if (age === null) {
      setError(lang === 'ko' ? '올바른 생년월일을 입력해주세요.' : 'Please enter a valid date of birth.');
      return;
    }

    if (age < 19) {
      setError(lang === 'ko' ? '만 19세 이상만 이용 가능합니다.' : 'You must be at least 19 years old to enter.');
      return;
    }

    onVerify();
  };

  return (
    <div className="w-full max-w-sm sm:max-w-md px-4 sm:px-6 text-center">
      <div className="space-y-3 sm:space-y-4 mb-5 sm:mb-6">
        <h2 className="text-lg sm:text-xl md:text-2xl font-sans font-bold text-soma uppercase tracking-wide">
          {lang === 'ko' ? '연령 확인' : 'Age Verification'}
        </h2>
        <p className="text-[11px] sm:text-sm md:text-base text-soma/70 whitespace-nowrap">
          {lang === 'ko'
            ? '이 사이트는 만 19세 이상만 이용 가능합니다.'
            : 'You must be at least 19 years old to enter this site.'}
        </p>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <p className="text-[10px] sm:text-xs md:text-sm text-soma/60 uppercase tracking-widest">
          {lang === 'ko' ? '생년월일' : 'Date of Birth'}
        </p>

        <div className="flex gap-2 sm:gap-3 justify-center">
          <input
            type="number"
            placeholder="YYYY"
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value.slice(0, 4))}
            className="w-20 sm:w-24 md:w-28 h-10 sm:h-12 text-center border-b-2 border-soma/30 focus:border-soma bg-transparent text-soma text-base sm:text-lg outline-none"
            maxLength={4}
          />
          <input
            type="number"
            placeholder="MM"
            value={birthMonth}
            onChange={(e) => setBirthMonth(e.target.value.slice(0, 2))}
            className="w-14 sm:w-16 md:w-20 h-10 sm:h-12 text-center border-b-2 border-soma/30 focus:border-soma bg-transparent text-soma text-base sm:text-lg outline-none"
            maxLength={2}
          />
          <input
            type="number"
            placeholder="DD"
            value={birthDay}
            onChange={(e) => setBirthDay(e.target.value.slice(0, 2))}
            className="w-14 sm:w-16 md:w-20 h-10 sm:h-12 text-center border-b-2 border-soma/30 focus:border-soma bg-transparent text-soma text-base sm:text-lg outline-none"
            maxLength={2}
          />
        </div>

        {error && (
          <p className="text-red-500 text-xs sm:text-sm">{error}</p>
        )}

        <Button
          onClick={handleVerify}
          className="w-full bg-soma text-white hover:bg-soma/80 text-xs sm:text-sm md:text-base py-3 sm:py-4 md:py-6 rounded-none uppercase tracking-widest mt-4 sm:mt-6"
        >
          {lang === 'ko' ? '확인' : 'Verify'}
        </Button>
      </div>

      <p className="text-[10px] sm:text-xs text-soma/50 mt-4 sm:mt-6">
        {lang === 'ko'
          ? '니코틴은 중독성 화학물질입니다.'
          : 'Nicotine is an addictive chemical.'}
      </p>
    </div>
  );
};

const LanguageToggle = ({ lang, setLang }) => {
  return (
    <div className="fixed top-0 right-0 z-[200] p-3 sm:p-4 md:p-6 lg:p-10">
      <motion.button
        onClick={() => setLang(lang === 'en' ? 'ko' : 'en')}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="text-white hover:bg-white hover:text-black font-sans text-[10px] sm:text-xs md:text-sm font-medium tracking-widest flex items-center gap-1 sm:gap-1.5 md:gap-2 bg-black/70 backdrop-blur-md px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full border border-white/30 shadow-lg"
      >
        <Globe size={12} className="sm:w-[14px] sm:h-[14px] md:w-[18px] md:h-[18px]" />
        {lang === 'en' ? 'KR' : 'EN'}
      </motion.button>
    </div>
  );
};

const ProductSection = ({ lang, onNotifyClick, isFixed = false, scrollY = 0, sectionStartY = 0, onExpandChange }) => {
  const tProd = TRANSLATIONS[lang].product;
  const [specsExpanded, setSpecsExpanded] = useState(false);
  const [deliveryExpanded, setDeliveryExpanded] = useState(false);

  // Notify parent when expand state changes (for z-index control)
  useEffect(() => {
    if (onExpandChange) {
      onExpandChange(specsExpanded || deliveryExpanded);
    }
  }, [specsExpanded, deliveryExpanded, onExpandChange]);

  // Layout ratio: padding:image:padding2:description:padding3 = 0.5:13.5:2:10:4 (total 30)
  return (
    <div className="w-full h-full relative bg-black">
      {/* Desktop Layout */}
      <div className="hidden md:block">
        {/* Single screen height layout with swiper */}
        <div className="flex h-screen">
          {/* Left Padding - 1.67% */}
          <div style={{ width: '1.67%' }} className="bg-black" />

          {/* Product Images - 45% with Swiper */}
          <div className="bg-black flex items-center justify-center" style={{ width: '45%' }}>
            <Swiper
              modules={[Pagination]}
              pagination={{ clickable: true }}
              spaceBetween={0}
              slidesPerView={1}
              className="w-full h-full [&_.swiper-pagination]:!bottom-8 [&_.swiper-pagination-bullet]:!w-2 [&_.swiper-pagination-bullet]:!h-2 [&_.swiper-pagination-bullet]:!bg-white/30 [&_.swiper-pagination-bullet-active]:!bg-white"
            >
              {ASSETS.PRODUCT_GALLERY.map((src, index) => (
                <SwiperSlide key={index}>
                  {src.includes('8.png') ? (
                    <div className="w-full h-full flex items-center justify-center bg-black">
                      <img
                        src={src}
                        alt={`SOMA Product ${index + 1}`}
                        className="w-[90%] h-[90%] object-contain"
                      />
                    </div>
                  ) : src.includes('6.png') ? (
                    <div className="w-full h-full flex items-center justify-center bg-black">
                      <img
                        src={src}
                        alt={`SOMA Product ${index + 1}`}
                        className="w-[95%] h-[95%] object-contain"
                      />
                    </div>
                  ) : (
                    <img
                      src={src}
                      alt={`SOMA Product ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Right section with product info - 53.33% */}
          <div className="h-screen bg-black" style={{ width: '53.33%' }}>
            <div className="h-full flex">
              {/* Middle Padding */}
              <div style={{ width: '12.5%' }} />

              {/* Product Info - Grid with exact vertical ratios */}
              {/* Rows: top-pad | 2pack+SOMA | Price+Flavor | btn | space | PD | D&R | GN | bottom-pad */}
              <div style={{ width: '62.5%' }} className="h-full grid grid-rows-[1.5fr_2fr_1fr_0.5fr_0.5fr_1.5fr_1fr_1fr_1fr_6fr] text-left">
                {/* Top Padding - 1.5 */}
                <div />

                {/* 2-pack and SOMA and White - 2 */}
                <div className="flex flex-col justify-start py-[8px]">
                  {/* 2-pack note - 16px */}
                  <p className="text-[16px] text-white/60">2-pack</p>
                  {/* Product Name - 28px semi-bold */}
                  <h2 className="text-[28px] font-sans font-semibold text-white leading-tight mt-[48px]">SOMA</h2>
                  {/* White - 14px */}
                  <p className="text-[14px] text-white/60 mt-[4px]">WHITE</p>
                </div>

                {/* Price - 1 (separate row, closer to button) */}
                <div className="flex flex-col justify-end">
                  {/* Price - 24px same as SOMA */}
                  <p className="text-[24px] font-light text-white">{tProd.price}</p>
                </div>

                {/* Space between price and button - 0.5 */}
                <div />

                {/* Naver Smart Store Button - 0.5 */}
                <div className="flex items-center">
                  <Button
                    className="w-full bg-white hover:bg-white/90 text-black text-[16px] py-[28px] rounded-none uppercase tracking-widest shadow-none transition-all font-normal"
                    onClick={() => window.open('https://smartstore.naver.com/somapouches/products/12929984158', '_blank')}
                  >
                    {tProd.naverStore}
                  </Button>
                </div>

                {/* Space between button and accordions - 1.5 */}
                <div />

                {/* Product Details - 1 */}
                <button
                  onClick={() => setSpecsExpanded(true)}
                  className="flex items-center justify-between w-full text-left text-white hover:text-white/70 transition-colors border-t border-white/20"
                >
                  <span className="text-[14px] uppercase tracking-widest">
                    {lang === 'ko' ? '제품 상세' : 'Product Details'}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </button>

                {/* Delivery & Returns - 1 */}
                <button
                  onClick={() => setDeliveryExpanded(true)}
                  className="flex items-center justify-between w-full text-left text-white hover:text-white/70 transition-colors border-t border-white/20"
                >
                  <span className="text-[14px] uppercase tracking-widest">
                    {lang === 'ko' ? '배송 및 반품' : 'Delivery & Returns'}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </button>

                {/* Get Notified - 1 */}
                <button
                  onClick={onNotifyClick}
                  className="flex items-center w-full text-left text-white hover:text-white/70 transition-colors border-t border-white/20"
                >
                  <span className="text-[14px] uppercase tracking-widest underline underline-offset-4">
                    {lang === 'ko' ? '출시 알림받기' : 'Get Notified'}
                  </span>
                </button>

                {/* Bottom Padding - 6 */}
                <div />
              </div>

              {/* Right Padding */}
              <div style={{ width: '25%' }} />
            </div>
          </div>
        </div>

        {/* Product Details Slide-out Panel */}
        <AnimatePresence>
          {specsExpanded && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black/40 z-40"
                onClick={() => setSpecsExpanded(false)}
              />
              {/* Panel */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="fixed top-0 right-0 h-full w-[400px] max-w-[90vw] bg-white z-50 shadow-2xl"
              >
                <div className="p-[32px] h-full overflow-y-auto">
                  {/* Title - 24px */}
                  <h3 className="text-[24px] font-sans text-black mb-[24px]">
                    {lang === 'ko' ? '제품 상세' : 'Product Details'}
                  </h3>

                  {/* Specs - 14px body, 8-12px spacing */}
                  <div className="space-y-[12px] text-[14px]">
                    <div className="flex justify-between gap-4 py-[8px] border-b border-black/10">
                      <span className="text-black/60">{tProd.specs.nicotine}</span>
                      <span className="text-black">6mg</span>
                    </div>
                    <div className="flex justify-between gap-4 py-[8px] border-b border-black/10">
                      <span className="text-black/60">{tProd.specs.pouches}</span>
                      <span className="text-black">20</span>
                    </div>
                    <div className="flex justify-between gap-4 py-[8px] border-b border-black/10">
                      <span className="text-black/60">{tProd.specs.flavor}</span>
                      <span className="text-black text-right">{tProd.specs.flavorDesc}</span>
                    </div>
                    <div className="flex justify-between gap-4 py-[8px] border-b border-black/10">
                      <span className="text-black/60">{tProd.specs.sensation}</span>
                      <span className="text-black">{tProd.specs.sensationDesc}</span>
                    </div>
                    <div className="flex justify-between gap-4 py-[8px] border-b border-black/10">
                      <span className="text-black/60">{tProd.specs.madeIn}</span>
                      <span className="text-black">{tProd.specs.madeInDesc}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Delivery & Returns Slide-out Panel */}
        <AnimatePresence>
          {deliveryExpanded && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black/40 z-40"
                onClick={() => setDeliveryExpanded(false)}
              />
              {/* Panel */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="fixed top-0 right-0 h-full w-[400px] max-w-[90vw] bg-white z-50 shadow-2xl"
              >
                <div className="p-[32px] h-full overflow-y-auto">
                  {/* Title - 24px */}
                  <h3 className="text-[24px] font-sans text-black mb-[24px]">
                    {lang === 'ko' ? '배송 및 반품' : 'Delivery & Returns'}
                  </h3>

                  {/* Content - 14px body, 8-12px spacing */}
                  <div className="space-y-[16px] text-[14px] text-black/80">
                    <div className="py-[8px] border-b border-black/10">
                      <p className="font-medium text-black mb-[8px]">{lang === 'ko' ? '배송' : 'Delivery'}</p>
                      <p>{lang === 'ko' ? '무료 배송 (프리런치 기간)' : 'Free shipping (Pre-launch period)'}</p>
                      <p className="mt-[8px]">{lang === 'ko' ? '2-3일 내 배송' : 'Delivery within 2-3 days'}</p>
                    </div>
                    <div className="py-[8px]">
                      <p className="font-medium text-black mb-[8px]">{lang === 'ko' ? '반품' : 'Returns'}</p>
                      <p>{lang === 'ko' ? '미개봉 제품 7일 이내 반품 가능' : 'Returns accepted within 7 days (unopened)'}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Layout - scrollable when expanded */}
      <div className="md:hidden h-full overflow-y-auto">
        {/* Product Images - Horizontal Swiper Carousel */}
        <div className="bg-black">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={0}
            slidesPerView={1}
            className="w-full aspect-square [&_.swiper-pagination]:!bottom-4 [&_.swiper-pagination-bullet]:!w-2 [&_.swiper-pagination-bullet]:!h-2 [&_.swiper-pagination-bullet]:!bg-white/30 [&_.swiper-pagination-bullet-active]:!bg-white"
          >
            {ASSETS.PRODUCT_GALLERY.map((src, index) => (
              <SwiperSlide key={index}>
                {src.includes('8.png') ? (
                  <div className="w-full h-full flex items-center justify-center bg-black">
                    <img
                      src={src}
                      alt={`SOMA Product ${index + 1}`}
                      className="w-[90%] h-[90%] object-contain"
                    />
                  </div>
                ) : src.includes('6.png') ? (
                  <div className="w-full h-full flex items-center justify-center bg-black">
                    <img
                      src={src}
                      alt={`SOMA Product ${index + 1}`}
                      className="w-[95%] h-[95%] object-contain"
                    />
                  </div>
                ) : (
                  <img
                    src={src}
                    alt={`SOMA Product ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Mobile Product Info */}
        <div className="p-8 sm:p-12 space-y-4 bg-black">
          {/* Product Title & Info */}
          <div>
            <h2 className="text-xl sm:text-2xl font-sans font-semibold text-white">SOMA, White</h2>
            <p className="text-[10px] sm:text-xs text-white/60 mt-1">{tProd.packNote}</p>
            <p className="text-lg sm:text-xl font-light text-white mt-2">{tProd.price}</p>
          </div>

          {/* Naver Store Button */}
          <Button
            className="w-full bg-white hover:bg-white/90 text-black text-xs sm:text-sm py-3 rounded-none uppercase tracking-widest shadow-none transition-all"
            onClick={() => window.open('https://smartstore.naver.com/somapouches/products/12929984158', '_blank')}
          >
            {tProd.naverStore}
          </Button>

          {/* Product Details - Expandable */}
          <div className="border-t border-white/20 pt-2">
            <button
              onClick={() => setSpecsExpanded(!specsExpanded)}
              className="flex items-center justify-between w-full text-left text-white hover:text-white/70 transition-colors py-2"
            >
              <span className="text-[10px] sm:text-xs uppercase tracking-widest">
                {lang === 'ko' ? '제품 상세' : 'Product Details'}
              </span>
              <span className={`transform transition-transform duration-300 ${specsExpanded ? 'rotate-180' : ''}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            <AnimatePresence>
              {specsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pb-2 space-y-1.5 text-xs">
                    <div className="flex justify-between gap-2">
                      <span className="text-white/60">{tProd.specs.nicotine}</span>
                      <span className="text-white">6mg</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-white/60">{tProd.specs.pouches}</span>
                      <span className="text-white">20</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-white/60">{tProd.specs.flavor}</span>
                      <span className="text-white text-right">{tProd.specs.flavorDesc}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-white/60">{tProd.specs.sensation}</span>
                      <span className="text-white">{tProd.specs.sensationDesc}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-white/60">{tProd.specs.madeIn}</span>
                      <span className="text-white">{tProd.specs.madeInDesc}</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Delivery & Returns - Expandable */}
          <div className="border-t border-white/20 pt-2">
            <button
              onClick={() => setDeliveryExpanded(!deliveryExpanded)}
              className="flex items-center justify-between w-full text-left text-white hover:text-white/70 transition-colors py-2"
            >
              <span className="text-[10px] sm:text-xs uppercase tracking-widest">
                {lang === 'ko' ? '배송 및 반품' : 'Delivery & Returns'}
              </span>
              <span className={`transform transition-transform duration-300 ${deliveryExpanded ? 'rotate-180' : ''}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            <AnimatePresence>
              {deliveryExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pb-2 space-y-1.5 text-xs text-white/70">
                    <p>{lang === 'ko' ? '• 무료 배송 (프리런치 기간)' : '• Free shipping (Pre-launch period)'}</p>
                    <p>{lang === 'ko' ? '• 2-3일 내 배송' : '• Delivery within 2-3 days'}</p>
                    <p>{lang === 'ko' ? '• 미개봉 제품 7일 이내 반품 가능' : '• Returns accepted within 7 days (unopened)'}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Get Notified */}
          <div className="border-t border-white/20 pt-4">
            <button
              onClick={onNotifyClick}
              className="text-[10px] sm:text-xs uppercase tracking-widest text-white hover:text-white/70 transition-colors underline underline-offset-4"
            >
              {lang === 'ko' ? '출시 알림받기' : 'Get Notified'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutView = ({ lang, onBack }) => {
  const t = TRANSLATIONS[lang].aboutPage;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }}
      className="fixed inset-0 min-h-screen bg-white z-[100] overflow-y-auto"
    >
      {/* Back Button */}
      <div className="fixed top-0 left-0 z-[110] p-3 sm:p-4 md:p-6 lg:p-10">
        <motion.button
          onClick={onBack}
          whileHover={{ x: -3 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="text-black hover:text-black/70 flex items-center gap-1.5 sm:gap-2 uppercase tracking-widest text-[10px] sm:text-xs md:text-sm font-bold"
        >
          <ArrowLeft size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" /> {t.back}
        </motion.button>
      </div>

      <div className="container max-w-4xl mx-auto pt-16 pb-12 sm:pt-20 sm:pb-16 md:pt-28 lg:pt-32 md:pb-24 px-4 sm:px-5 md:px-6">
        <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-sans font-bold text-black mb-3 sm:mb-4 md:mb-6">{t.title}</h1>

        {/* Intro */}
        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-black/80 leading-relaxed mb-4 sm:mb-6 md:mb-10 lg:mb-12 font-light">
          {t.intro}
        </p>

        <div className="space-y-3 sm:space-y-4 md:space-y-6 lg:space-y-8">
          <p className="text-xs sm:text-sm md:text-base text-black/80 leading-relaxed">{t.p1}</p>
          <p className="text-xs sm:text-sm md:text-base text-black/80 leading-relaxed">{t.p2}</p>
        </div>

        {/* Swedish Pouches Section */}
        <div className="mt-8 sm:mt-10 md:mt-14 lg:mt-16 pt-6 sm:pt-8 md:pt-10 lg:pt-12 border-t border-black/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 items-start">
            {/* Text Content */}
            <div className="order-2 md:order-1">
              <h2 className="text-lg sm:text-xl md:text-3xl lg:text-4xl font-sans font-bold text-black mb-3 sm:mb-4 md:mb-6 lg:mb-8">{t.subTitle}</h2>

              <div className="space-y-3 sm:space-y-4 md:space-y-5 lg:space-y-6">
                <p className="text-xs sm:text-sm md:text-base text-black/80 leading-relaxed">{t.heritage1}</p>
                <p className="text-xs sm:text-sm md:text-base text-black/80 leading-relaxed">{t.heritage2}</p>

                {t.heritageList && (
                  <ul className="space-y-1.5 sm:space-y-2 md:space-y-3 ml-1">
                    {t.heritageList.map((item, index) => (
                      <li key={index} className="text-xs sm:text-sm md:text-base text-black/80 leading-relaxed flex items-start gap-1.5 sm:gap-2 md:gap-3">
                        <span className="text-black">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Deconstructed Image */}
            <div className="flex justify-center order-1 md:order-2">
              <img
                src={ASSETS.SOMA_DECONSTRUCTED}
                alt="SOMA Deconstructed"
                className="w-full max-w-[160px] sm:max-w-[200px] md:max-w-[280px] lg:max-w-[320px] h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Terms of Service Page
const TermsView = ({ lang, onBack }) => {
  const t = TRANSLATIONS[lang].terms;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }}
      className="fixed inset-0 min-h-screen bg-white z-[100] overflow-y-auto"
    >
      {/* Back Button */}
      <div className="fixed top-0 left-0 z-[110] p-3 sm:p-4 md:p-6 lg:p-10">
        <motion.button
            onClick={onBack}
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="text-black hover:text-black/70 flex items-center gap-1.5 sm:gap-2 uppercase tracking-widest text-[10px] sm:text-xs md:text-sm font-bold"
        >
            <ArrowLeft size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" /> {t.back}
        </motion.button>
      </div>

      <div className="container max-w-4xl mx-auto pt-16 pb-12 sm:pt-20 sm:pb-16 md:pt-28 lg:pt-32 md:pb-24 px-4 sm:px-5 md:px-6">
        <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-sans font-bold text-black mb-1.5 sm:mb-2 md:mb-4">{t.title}</h1>
        <p className="text-[10px] sm:text-xs md:text-sm text-black/60 mb-4 sm:mb-6 md:mb-10 lg:mb-12">{t.lastUpdated}</p>

        <div className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8">
          {t.sections.map((section, index) => (
            <div key={index} className="space-y-1.5 sm:space-y-2 md:space-y-3">
              <h2 className="text-xs sm:text-sm md:text-lg lg:text-xl font-semibold text-black">{section.title}</h2>
              <p className="text-[10px] sm:text-xs md:text-sm lg:text-base text-black/80 leading-relaxed">{section.content}</p>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Privacy Policy Page
const PrivacyView = ({ lang, onBack }) => {
  const t = TRANSLATIONS[lang].privacy;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }}
      className="fixed inset-0 min-h-screen bg-white z-[100] overflow-y-auto"
    >
      {/* Back Button */}
      <div className="fixed top-0 left-0 z-[110] p-3 sm:p-4 md:p-6 lg:p-10">
        <motion.button
            onClick={onBack}
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="text-black hover:text-black/70 flex items-center gap-1.5 sm:gap-2 uppercase tracking-widest text-[10px] sm:text-xs md:text-sm font-bold"
        >
            <ArrowLeft size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" /> {t.back}
        </motion.button>
      </div>

      <div className="container max-w-4xl mx-auto pt-16 pb-12 sm:pt-20 sm:pb-16 md:pt-28 lg:pt-32 md:pb-24 px-4 sm:px-5 md:px-6">
        <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-sans font-bold text-black mb-1.5 sm:mb-2 md:mb-4">{t.title}</h1>
        <p className="text-[10px] sm:text-xs md:text-sm text-black/60 mb-4 sm:mb-6 md:mb-10 lg:mb-12">{t.lastUpdated}</p>

        <div className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8">
          {t.sections.map((section, index) => (
            <div key={index} className="space-y-1.5 sm:space-y-2 md:space-y-3">
              <h2 className="text-xs sm:text-sm md:text-lg lg:text-xl font-semibold text-black">{section.title}</h2>
              <p className="text-[10px] sm:text-xs md:text-sm lg:text-base text-black/80 leading-relaxed">{section.content}</p>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const Footer = ({ lang, onNavigate }) => {
  const t = TRANSLATIONS[lang].footer;
  const tModal = TRANSLATIONS[lang].modal;
  const [footerEmail, setFooterEmail] = useState('');
  const [footerLoading, setFooterLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [footerMessage, setFooterMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleFooterEmailSubmit = async () => {
    if (!footerEmail || !footerEmail.includes('@')) {
      setFooterMessage(lang === 'en' ? 'Please enter a valid email' : '유효한 이메일을 입력해주세요');
      setIsSuccess(false);
      return;
    }

    setFooterLoading(true);
    setFooterMessage('');

    const result = await sendVerificationEmail(footerEmail, lang);

    if (result.success) {
      setIsSuccess(true);
      setFooterMessage(tModal.success);
      setFooterEmail('');
    } else if (result.error === 'duplicate') {
      setIsSuccess(false);
      setFooterMessage(tModal.alreadySubscribed);
    } else {
      setIsSuccess(false);
      setFooterMessage(result.message);
    }

    setFooterLoading(false);
  };

  const handleFooterGoogleSignUp = async () => {
    setGoogleLoading(true);
    setFooterMessage('');

    const result = await signUpWithGoogle();

    if (result.success) {
      if (result.isNew) {
        setIsSuccess(true);
        setFooterMessage(tModal.successSimple);
      } else {
        setIsSuccess(false);
        setFooterMessage(tModal.alreadySubscribed);
      }
    } else {
      setIsSuccess(false);
      setFooterMessage(lang === 'ko' ? (result.message === 'Sign up cancelled' ? '가입이 취소되었습니다' : result.message) : result.message);
    }

    setGoogleLoading(false);
  };

  return (
    <footer className="relative z-30 bg-black mt-0 p-0">
      <div className="w-full">
        {/* Black content box */}
        <div className="bg-black px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 py-12 sm:py-16 md:py-20 lg:py-24">

          {/* Desktop Layout (lg+) - Wide spread brand-centered */}
          <div className="hidden lg:block">
            {/* Top Section - Logo left, Email center, Links right */}
            <div className="grid grid-cols-3 items-start gap-8 mb-16 2xl:mb-20">
              {/* Left - Logo & Slogan */}
              <div className="flex flex-col items-start">
                <img
                  src={ASSETS.SOMA_LOGO}
                  alt="SOMA"
                  className="h-16 xl:h-20 2xl:h-24 mb-2 2xl:mb-3 -ml-7 xl:-ml-9 2xl:-ml-11"
                />
                <p className="text-white/50 text-[10px] xl:text-xs 2xl:text-sm uppercase tracking-[0.2em] text-left">
                  {lang === 'ko' ? '타협 없는 선명함' : 'Clarity Without Compromise'}
                </p>
              </div>

              {/* Center - Email Subscription */}
              <div className="flex flex-col items-center">
                <p className="text-white/60 text-xs xl:text-sm 2xl:text-base mb-4 2xl:mb-6">
                  {lang === 'ko' ? 'SOMA의 최신 소식을 받아보세요' : 'Stay inspired with SOMA'}
                </p>
                <div className="flex gap-2 w-full max-w-sm 2xl:max-w-md">
                  <Input
                    placeholder={t.emailPlaceholder}
                    value={footerEmail}
                    onChange={(e) => setFooterEmail(e.target.value)}
                    className="flex-1 bg-transparent border border-white/30 hover:border-white/50 focus:border-white text-white placeholder:text-white/40 h-10 xl:h-11 2xl:h-12 rounded-none focus-visible:ring-0 px-3 2xl:px-4 text-xs xl:text-sm 2xl:text-base transition-colors"
                  />
                  <Button
                    onClick={handleFooterEmailSubmit}
                    disabled={footerLoading || googleLoading}
                    className="bg-white text-black hover:bg-white/80 rounded-none px-5 xl:px-6 2xl:px-8 h-10 xl:h-11 2xl:h-12"
                  >
                    {footerLoading ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={18} className="2xl:w-5 2xl:h-5" />}
                  </Button>
                </div>
                {footerMessage && (
                  <p className={`text-[10px] xl:text-xs 2xl:text-sm mt-2 ${isSuccess ? 'text-green-600' : 'text-red-500'}`}>
                    {footerMessage}
                  </p>
                )}
              </div>

              {/* Right - Links */}
              <div className="flex flex-col items-end gap-3 2xl:gap-4">
                <a
                  href="https://www.instagram.com/somanicotine/?next=%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white opacity-50 hover:opacity-100 transition-opacity flex items-center gap-2 2xl:gap-3"
                >
                  <Instagram size={16} className="2xl:w-5 2xl:h-5" />
                  <span className="text-[10px] xl:text-xs 2xl:text-sm uppercase tracking-widest">{t.links.instagram}</span>
                </a>
                <button
                  onClick={() => onNavigate('about')}
                  className="text-white text-[10px] xl:text-xs 2xl:text-sm uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity"
                >
                  {t.links.about}
                </button>
                <button
                  onClick={() => onNavigate('terms')}
                  className="text-white text-[10px] xl:text-xs 2xl:text-sm uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity"
                >
                  {t.links.terms}
                </button>
                <button
                  onClick={() => onNavigate('privacy')}
                  className="text-white text-[10px] xl:text-xs 2xl:text-sm uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity"
                >
                  {t.links.privacy}
                </button>
              </div>
            </div>

            {/* Bottom - Legal centered */}
            <div className="border-t border-white/10 pt-8 2xl:pt-10 flex justify-between items-center">
              <p className="text-[9px] xl:text-[10px] 2xl:text-xs text-white/30">{t.warning}</p>
              <p className="text-[9px] xl:text-[10px] 2xl:text-xs uppercase tracking-widest text-white/25">{t.copyright}</p>
            </div>
          </div>

          {/* Mobile/Tablet Layout (below lg) - Centered brand-focused */}
          <div className="lg:hidden">
            {/* Brand Section - Centered */}
            <div className="text-center mb-12 sm:mb-16">
              <img
                src={ASSETS.SOMA_LOGO}
                alt="SOMA"
                className="h-12 sm:h-14 mx-auto mb-4 sm:mb-6"
              />
              <p className="text-white/50 text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em]">
                {lang === 'ko' ? '타협 없는 선명함' : 'Clarity Without Compromise'}
              </p>
            </div>

            {/* Email Subscription - Centered */}
            <div className="max-w-md mx-auto mb-12 sm:mb-16">
              <p className="text-center text-white/60 text-xs sm:text-sm mb-4 sm:mb-6">
                {lang === 'ko' ? 'SOMA의 최신 소식을 받아보세요' : 'Stay inspired with SOMA'}
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder={t.emailPlaceholder}
                  value={footerEmail}
                  onChange={(e) => setFooterEmail(e.target.value)}
                  className="flex-1 bg-transparent border border-white/30 hover:border-white/50 focus:border-white text-white placeholder:text-white/40 h-10 sm:h-11 rounded-none focus-visible:ring-0 px-3 text-xs sm:text-sm transition-colors"
                />
                <Button
                  onClick={handleFooterEmailSubmit}
                  disabled={footerLoading || googleLoading}
                  className="bg-white text-black hover:bg-white/80 rounded-none px-4 sm:px-6 h-10 sm:h-11"
                >
                  {footerLoading ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
                </Button>
              </div>
              {footerMessage && (
                <p className={`text-[10px] sm:text-xs mt-2 text-center ${isSuccess ? 'text-green-600' : 'text-red-500'}`}>
                  {footerMessage}
                </p>
              )}
            </div>

            {/* Links - Horizontal Centered */}
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 mb-10 sm:mb-12 text-white">
              <a
                href="https://www.instagram.com/somanicotine/?next=%2F"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <Instagram size={18} className="sm:w-5 sm:h-5" />
              </a>
              <span className="w-px h-4 bg-white/20 hidden sm:block" />
              <button
                onClick={() => onNavigate('about')}
                className="text-[10px] sm:text-xs uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity"
              >
                {t.links.about}
              </button>
              <button
                onClick={() => onNavigate('terms')}
                className="text-[10px] sm:text-xs uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity"
              >
                {t.links.terms}
              </button>
              <button
                onClick={() => onNavigate('privacy')}
                className="text-[10px] sm:text-xs uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity"
              >
                {t.links.privacy}
              </button>
            </div>

            {/* Bottom - Legal */}
            <div className="text-center space-y-2">
              <p className="text-[9px] sm:text-[10px] text-white/30">{t.warning}</p>
              <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/25">{t.copyright}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const GetNotifiedModal = ({ isOpen, onClose, lang }) => {
  const t = TRANSLATIONS[lang].modal;
  const [modalEmail, setModalEmail] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const modalVideoRef = useRef(null);

  // Force video play when modal opens
  useEffect(() => {
    if (isOpen && modalVideoRef.current) {
      const video = modalVideoRef.current;
      video.muted = true;
      video.play().catch(() => {});
    }
  }, [isOpen]);

  const handleEmailSignUp = async () => {
    if (!modalEmail || !modalEmail.includes('@')) {
      setModalMessage(lang === 'en' ? 'Please enter a valid email' : '유효한 이메일을 입력해주세요');
      return;
    }

    setModalLoading(true);
    setModalMessage('');

    const result = await sendVerificationEmail(modalEmail, lang);

    if (result.success) {
      setIsSuccess(true);
      setModalMessage(t.success);
      setModalEmail('');
      setTimeout(() => {
        onClose();
        setModalMessage('');
        setIsSuccess(false);
      }, 3000);
    } else {
      setModalMessage(result.message);
    }

    setModalLoading(false);
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    setModalMessage('');

    const result = await signUpWithGoogle();

    if (result.success) {
      if (result.isNew) {
        setIsSuccess(true);
        setModalMessage(t.successSimple);
        setTimeout(() => {
          onClose();
          setModalMessage('');
          setIsSuccess(false);
        }, 2000);
      } else {
        setIsSuccess(false);
        setModalMessage(t.alreadySubscribed);
      }
    } else {
      setIsSuccess(false);
      setModalMessage(lang === 'ko' ? (result.message === 'Sign up cancelled' ? '가입이 취소되었습니다' : result.message) : result.message);
    }

    setGoogleLoading(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="max-w-full w-full h-full p-0 border-none bg-transparent z-[60] [&>button.absolute]:hidden">
             <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.4,
                  ease: [0.4, 0, 0.2, 1]
                }}
                className="absolute inset-0 w-full h-full overflow-hidden"
             >
                <video
                    ref={modalVideoRef}
                    src={isMobile() ? ASSETS.LANDING_VIDEO_MOBILE : ASSETS.LANDING_VIDEO}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto object-cover pointer-events-none [&::-webkit-media-controls]:hidden [&::-webkit-media-controls-enclosure]:hidden [&::-webkit-media-controls-panel]:hidden"
                    style={{ WebkitAppearance: 'none' }}
                />
                <div className="absolute inset-0 bg-white/90" />

                {/* Back button - positioned at top left of modal */}
                <button
                    onClick={onClose}
                    className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-10 md:left-10 text-black hover:opacity-70 z-10 flex items-center gap-1.5 sm:gap-2 uppercase tracking-widest text-[10px] sm:text-xs md:text-sm font-bold"
                >
                    <ArrowLeft size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    {lang === 'ko' ? '뒤로' : 'Back'}
                </button>

                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 sm:p-5 md:p-6 overflow-y-auto">
                    <motion.div
                        initial={{ y: 40, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.15,
                          duration: 0.6,
                          ease: [0.16, 1, 0.3, 1]
                        }}
                        className="max-w-sm sm:max-w-md w-full space-y-4 sm:space-y-6 md:space-y-8 text-center"
                    >
                        <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-sans text-black uppercase tracking-wide">{t.title}</h2>

                        <div className="space-y-3 sm:space-y-4 md:space-y-6 pt-3 sm:pt-4 md:pt-8">
                            {/* Email Input */}
                            <div className="space-y-1.5 sm:space-y-2 text-left">
                                <label className="text-[10px] sm:text-xs md:text-sm text-black uppercase tracking-widest ml-1">{t.emailLabel}</label>
                                <Input
                                    type="email"
                                    placeholder="email@example.com"
                                    value={modalEmail}
                                    onChange={(e) => setModalEmail(e.target.value)}
                                    className="bg-transparent border-b-2 border-black text-black placeholder:text-black/40 text-sm sm:text-base md:text-xl h-9 sm:h-10 md:h-12 rounded-none focus-visible:ring-0 px-1.5 sm:px-2"
                                />
                            </div>

                            {/* Email Sign Up Button */}
                            <Button
                                onClick={handleEmailSignUp}
                                disabled={modalLoading || googleLoading}
                                className="w-full bg-black text-white hover:bg-black/80 text-xs sm:text-sm md:text-base lg:text-lg py-3 sm:py-4 md:py-5 lg:py-6 rounded-none uppercase tracking-widest"
                            >
                                {modalLoading ? <Loader2 className="animate-spin" size={18} /> : t.signUp}
                            </Button>

                            {/* Divider */}
                            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                              <div className="flex-1 h-px bg-black/20"></div>
                              <span className="text-[10px] sm:text-xs md:text-sm text-black/60">{t.orContinueWith}</span>
                              <div className="flex-1 h-px bg-black/20"></div>
                            </div>

                            {/* Google Sign Up Button */}
                            <Button
                                onClick={handleGoogleSignUp}
                                disabled={modalLoading || googleLoading}
                                variant="outline"
                                className="w-full border-black text-black hover:bg-black/10 text-xs sm:text-sm md:text-base lg:text-lg py-3 sm:py-4 md:py-5 lg:py-6 rounded-none uppercase tracking-widest flex items-center justify-center gap-1.5 sm:gap-2 md:gap-3"
                            >
                                {googleLoading ? (
                                  <Loader2 className="animate-spin" size={18} />
                                ) : (
                                  <>
                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
                                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    {t.google}
                                  </>
                                )}
                            </Button>

                            {/* Message */}
                            {modalMessage && (
                              <p className={`text-[10px] sm:text-xs md:text-sm ${isSuccess ? 'text-green-600' : 'text-red-500'}`}>
                                {modalMessage}
                              </p>
                            )}
                        </div>
                    </motion.div>
                </div>
             </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default function App() {
  const [isVerified, setIsVerified] = useState(false); // Age verification status
  const [shouldAnimate, setShouldAnimate] = useState(false); // Only true when user clicks verify
  const [view, setView] = useState('home'); // 'home', 'about', 'terms', 'privacy'
  const [lang, setLang] = useState('ko');
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [isProductExpanded, setIsProductExpanded] = useState(false); // Track product detail expansion for z-index
  const [isLoaded, setIsLoaded] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const verificationAttempted = useRef(false);
  const [scrollY, setScrollY] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const savedScrollPosition = useRef(0);
  const videoRef = useRef(null);

  // Force video autoplay on ALL devices regardless of settings
  useEffect(() => {
    const forcePlay = (video) => {
      if (!video) return;
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const attemptPlay = () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Silently retry
            setTimeout(attemptPlay, 100);
          });
        }
      };
      attemptPlay();
    };

    // Play main video
    forcePlay(videoRef.current);

    // Global interaction handler - plays video on ANY user interaction
    const handleAnyInteraction = () => {
      forcePlay(videoRef.current);
    };

    // Listen to ALL possible interaction events
    const events = ['touchstart', 'touchend', 'click', 'scroll', 'keydown', 'mousemove'];
    events.forEach(event => {
      document.addEventListener(event, handleAnyInteraction, { passive: true });
    });

    // Visibility change - resume when tab becomes visible
    const handleVisibility = () => {
      if (!document.hidden) {
        forcePlay(videoRef.current);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Periodic check - ensure video is playing
    const intervalId = setInterval(() => {
      if (videoRef.current && videoRef.current.paused) {
        forcePlay(videoRef.current);
      }
    }, 1000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleAnyInteraction);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(intervalId);
    };
  }, [isLoaded]);

  // Navigate to a view, saving scroll position
  const navigateTo = (newView) => {
    if (newView !== 'home') {
      savedScrollPosition.current = window.scrollY;
    }
    setView(newView);
    // Don't scroll main page - overlay pages are fixed position
  };

  // Go back to home, restoring scroll position
  const goBackHome = () => {
    setView('home');
    // Restore scroll position after view change
    requestAnimationFrame(() => {
      window.scrollTo(0, savedScrollPosition.current);
    });
  };

  // Track scroll position for parallax and window width for horizontal scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleVerify = () => {
    setShouldAnimate(true);
    setIsVerified(true);
  };

  // Enable smooth scrolling on desktop
  useSmoothScroll(view === 'home' || view === 'about');

  useEffect(() => {
    setIsLoaded(true);

    const token = getVerificationToken();
    if (token && !verificationAttempted.current) {
      verificationAttempted.current = true;
      verifyEmailToken(token).then((result) => {
        if (result.success) {
          setVerificationStatus('success');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setVerificationStatus('error');
        }
        setTimeout(() => setVerificationStatus(null), 5000);
      });
    }
  }, []);

  if (!isLoaded) return null;

  const showHeroSection = view === 'home';

  // Parallax calculations (only when verified)
  const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const scrollProgress = isVerified ? Math.min(scrollY / windowHeight, 1) : 0;
  const logoTranslateY = -scrollProgress * (windowHeight / 2 + 200);

  return (
    <div className={`relative text-soma selection:bg-soma/20 font-sans ${isVerified ? 'min-h-screen' : 'h-screen overflow-hidden'}`}>

      {/* Background - Video block moves up with scroll like a curtain */}
      <div
        className="fixed inset-0"
        style={{
          zIndex: 10,
          transform: `translateY(${isVerified && scrollY > windowHeight ? -(scrollY - windowHeight) : 0}px)`,
        }}
      >
        <motion.div
          initial={{ filter: isVerified ? 'blur(0px)' : 'blur(60px)' }}
          animate={{ filter: isVerified ? 'blur(0px)' : 'blur(60px)' }}
          transition={shouldAnimate ? { duration: 2, ease: [0.16, 1, 0.3, 1] } : { duration: 0 }}
          className="absolute inset-0 overflow-hidden"
        >
          <video
            ref={videoRef}
            src={isMobile() ? ASSETS.LANDING_VIDEO_MOBILE : ASSETS.LANDING_VIDEO}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none [&::-webkit-media-controls]:hidden [&::-webkit-media-controls-enclosure]:hidden [&::-webkit-media-controls-panel]:hidden"
            style={{ WebkitAppearance: 'none' }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-black/70" />
      </div>

      {/* Language Toggle - always visible */}
      <LanguageToggle lang={lang} setLang={setLang} />

      {/* Ticker Banner - follows video from bottom, scrolls up with it */}
      {isVerified && scrollY > windowHeight && (
        <div
          className="fixed left-0 right-0 z-10 bg-black overflow-hidden py-1.5 sm:py-2"
          style={{
            bottom: 0,
            transform: `translateY(${-(scrollY - windowHeight)}px)`,
          }}
        >
          <div className="animate-marquee whitespace-nowrap flex">
            {[...Array(10)].map((_, i) => (
              <span key={i} className="mx-4 sm:mx-6 md:mx-8 text-soma text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.15em] md:tracking-[0.2em] uppercase font-light">
                PRE-LAUNCH EXCLUSIVE · FREE SHIPPING · PRE-LAUNCH EXCLUSIVE · FREE SHIPPING
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Hero Section with Parallax */}
      {showHeroSection && (
        <>
          {/* Logo and Swedish Made container - absolute center position */}
          <div className="fixed inset-0 z-10 pointer-events-none">
            {/* Logo + Swedish Made container - moves together */}
            <div
              className="absolute left-1/2 flex flex-col items-center"
              style={{
                top: isVerified ? '50%' : '32%',
                transform: `translate(-50%, -50%) scale(${isVerified ? 1.5 : 1})`,
                transition: shouldAnimate ? 'top 2.5s cubic-bezier(0.16, 1, 0.3, 1), transform 2.5s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
                opacity: isVerified && scrollY > 50 ? Math.max(0, 1 - (scrollY - 50) / 200) : 1
              }}
            >
              {/* Logo */}
              <img
                src={ASSETS.SOMA_LOGO}
                alt="SOMA Logo"
                className="w-[14rem] sm:w-[18rem] md:w-[22rem] lg:w-[24rem] h-auto"
              />
              {/* Swedish Made Nicotine Pouches - directly below logo */}
              <img
                src={ASSETS.SWEDISH_MADE_TEXT}
                alt="Swedish Made Nicotine Pouches"
                className="w-[14rem] sm:w-[18rem] md:w-[22rem] lg:w-[24rem] h-auto -mt-[5.7rem] sm:-mt-[7.7rem] md:-mt-[10rem] lg:-mt-[11rem]"
                style={{
                  opacity: isVerified ? 1 : 0,
                  transition: shouldAnimate ? 'opacity 1.5s ease-out 1s' : 'none'
                }}
              />
            </div>


            {/* Age Verification Form - upper position */}
            {!isVerified && (
              <div className="absolute left-1/2 -translate-x-1/2 top-[42%] sm:top-[45%] pointer-events-auto">
                <AgeVerifyScreen
                  lang={lang}
                  setLang={setLang}
                  onVerify={handleVerify}
                />
              </div>
            )}
          </div>

          {/* Pre-Launch Sale - moves up with video background like a curtain */}
          {isVerified && scrollY > 280 && scrollY < windowHeight * 2 && (
            <div
              className="fixed inset-0 flex items-center justify-center pointer-events-none px-4"
              style={{
                zIndex: 20,
                opacity: Math.min(1, (scrollY - 280) / 250),
                transform: `translateY(${scrollY > windowHeight ? -(scrollY - windowHeight) : 0}px)`,
              }}
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light text-soma uppercase tracking-[0.12em] sm:tracking-[0.15em] md:tracking-[0.2em] text-center">
                Pre-launch Sale
              </h2>
            </div>
          )}

          {/* Product Section + Footer - revealed by curtain, then scrolls together */}
          {isVerified && (
            <>
              {/* Fixed Product Section - behind video curtain */}
              <div
                className="fixed inset-0 bg-black"
                style={{ zIndex: 5 }}
              >
                <ProductSection lang={lang} onNotifyClick={() => setIsNotifyOpen(true)} isFixed={true} scrollY={scrollY} sectionStartY={windowHeight * 2} onExpandChange={setIsProductExpanded} />
              </div>

              {/* Scrollable spacer + Footer */}
              <div className="relative pointer-events-none" style={{ zIndex: 5 }}>
                {/* Spacer: increases when product details are expanded on mobile */}
                <div className={isProductExpanded ? "h-[360vh] md:h-[310vh]" : "h-[310vh]"} />

                {/* Footer - scrolls up pushing product section */}
                <div className="relative bg-black pointer-events-auto">
                  {/* Ticker Banner Divider between Product and Footer */}
                  <div className="bg-black overflow-hidden py-3 sm:py-4">
                    <div className="animate-marquee whitespace-nowrap flex">
                      {[...Array(10)].map((_, i) => (
                        <span key={i} className="mx-4 sm:mx-6 md:mx-8 text-soma text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.15em] md:tracking-[0.2em] uppercase font-light">
                          PRE-LAUNCH EXCLUSIVE · FREE SHIPPING · PRE-LAUNCH EXCLUSIVE · FREE SHIPPING
                        </span>
                      ))}
                    </div>
                  </div>
                  <Footer lang={lang} onNavigate={navigateTo} />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Main Content */}
      <AnimatePresence mode="wait">
        {view === 'about' && (
          <AboutView
            key="about"
            lang={lang}
            onBack={goBackHome}
          />
        )}

        {view === 'terms' && (
          <TermsView
            key="terms"
            lang={lang}
            onBack={goBackHome}
          />
        )}

        {view === 'privacy' && (
          <PrivacyView
            key="privacy"
            lang={lang}
            onBack={goBackHome}
          />
        )}
      </AnimatePresence>

      <GetNotifiedModal
        isOpen={isNotifyOpen}
        onClose={() => setIsNotifyOpen(false)}
        lang={lang}
      />

      {/* Email Verification Status Toast */}
      <AnimatePresence>
        {verificationStatus && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }}
            className={`fixed bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 md:py-4 rounded-lg shadow-lg max-w-[90%] sm:max-w-md ${
              verificationStatus === 'success'
                ? 'bg-green-600 text-white'
                : 'bg-red-600 text-white'
            }`}
          >
            <p className="font-medium text-xs sm:text-sm md:text-base text-center">
              {verificationStatus === 'success'
                ? (lang === 'ko' ? '이메일 인증 완료! 구독되었습니다.' : 'Email verified! You are now subscribed.')
                : (lang === 'ko' ? '인증 실패. 다시 시도해주세요.' : 'Verification failed. Please try again.')}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
